<template>
    <div class="template-message-list">
        <div class="page-header">
            <div class="header-left">
                <h2>{{ $t('whatsapp_business_api.templateListTitle') }}</h2>
                <span class="template-count" v-if="templates.length">
                    {{ $t('whatsapp_business_api.templateListShownTotal', { shown: filteredTemplates.length, total: templates.length }) }}
                </span>
            </div>
            <div class="header-actions">
                <a-button type="primary" @click="gotoCreatePage">
                    <a-icon type="plus" /> {{ $t('whatsapp_business_api.templateListCreateButton') }}
                </a-button>
            </div>
        </div>

        <div class="filters-bar">
            <a-input-search
                v-model="searchQuery"
                :placeholder="$t('whatsapp_business_api.templateListSearchPlaceholder')"
                style="width: 250px"
                allowClear
            />
            <a-select v-model="categoryFilter" :placeholder="$t('whatsapp_business_api.templateListAllCategories')" style="width: 150px" allowClear>
                <a-select-option value="">{{ $t('whatsapp_business_api.templateListAllCategories') }}</a-select-option>
                <a-select-option value="MARKETING">{{ $t('whatsapp_business_api.marketing') }}</a-select-option>
                <a-select-option value="UTILITY">{{ $t('whatsapp_business_api.utility') }}</a-select-option>
                <a-select-option value="AUTHENTICATION">{{ $t('whatsapp_business_api.authentication') }}</a-select-option>
            </a-select>
            <a-select v-model="statusFilter" :placeholder="$t('whatsapp_business_api.templateListAllStatus')" style="width: 140px" allowClear>
                <a-select-option value="">{{ $t('whatsapp_business_api.templateListAllStatus') }}</a-select-option>
                <a-select-option value="APPROVED">{{ $t('whatsapp_business_api.templateListStatusApproved') }}</a-select-option>
                <a-select-option value="PENDING">{{ $t('whatsapp_business_api.templateListStatusPending') }}</a-select-option>
                <a-select-option value="REJECTED">{{ $t('whatsapp_business_api.templateListStatusRejected') }}</a-select-option>
            </a-select>
            <a-button @click="refreshTemplates" :loading="refreshing">
                <a-icon type="sync" :spin="refreshing" /> {{ $t('whatsapp_business_api.templateListRefresh') }}
            </a-button>
            <div class="spacer"></div>
            <div class="pagination-info">
                {{ $t('whatsapp_business_api.templateListTotalTemplates', { count: filteredTemplates.length }) }}
            </div>
        </div>

        <div class="content-area">
            <div class="table-container">
                <a-table
                    :columns="columns"
                    :data-source="filteredTemplates"
                    :row-key="(record) => record.id"
                    :pagination="tablePagination"
                    :row-class-name="getRowClassName"
                    :customRow="customRow"
                >
                    <template slot="name" slot-scope="text, record">
                        <div class="template-name-cell">
                            <span class="name">{{ getTemplateName(record) }}</span>
                            <a-tooltip :title="copiedId === getTemplateId(record) ? $t('whatsapp_business_api.templateListCopied') : $t('whatsapp_business_api.templateListCopy')">
                                <span
                                    class="template-id"
                                    :class="{ 'copied': copiedId === getTemplateId(record) }"
                                    @click.stop="copyTemplateId(record)"
                                >
                                    <a-icon type="copy" /> {{ $t('whatsapp_business_api.templateListIdLabel') }} {{ getTemplateId(record) }}
                                </span>
                            </a-tooltip>
                            <span class="language">{{ getLanguageDisplay(record) }}</span>
                            <span class="preview-text">{{ getBodyPreview(record) }}</span>
                        </div>
                    </template>
                    <template slot="category" slot-scope="text, record">
                        <a-tag :color="getCategoryColor(getTemplateCategory(record))">
                            {{ getCategoryDisplay(record) }}
                        </a-tag>
                    </template>
                    <template slot="status" slot-scope="text, record">
                        <div class="status-cell">
                            <div class="status-content">
                                <span class="status-badge" :class="getStatusClass(getTemplateStatus(record))">
                                    <a-icon
                                        v-if="getTemplateStatus(record) === 'PENDING'"
                                        type="loading"
                                        class="pending-icon"
                                    />
                                    <a-icon
                                        v-else-if="getTemplateStatus(record) === 'APPROVED'"
                                        type="check-circle"
                                        theme="filled"
                                    />
                                    <a-icon
                                        v-else-if="['REJECTED', 'FAILED'].includes(getTemplateStatus(record))"
                                        type="close-circle"
                                        theme="filled"
                                    />
                                    {{ getStatusText(getTemplateStatus(record)) }}
                                </span>
                                <span v-if="getQualityScore(record)" class="quality-badge" :class="getQualityClass(record)">
                                    {{ getQualityText(record) }}
                                </span>
                            </div>
                            <div
                                v-if="['REJECTED', 'FAILED'].includes(getTemplateStatus(record)) && getRejectionInfo(record)"
                                class="rejection-info"
                            >
                                <div class="rejection-reason">
                                    <strong>{{ getRejectedReason(record) }}</strong>
                                </div>
                                <div v-if="getRejectionRecommendation(record)" class="rejection-recommendation">
                                    {{ getRejectionRecommendation(record) }}
                                </div>
                            </div>
                        </div>
                    </template>
                    <template slot="lastEdited" slot-scope="text, record">
                        <span>{{ formatDate(record.updatedAt || record.createdAt) }}</span>
                    </template>
                    <template slot="actions" slot-scope="text, record">
                        <a-dropdown :trigger="['click']">
                            <a-button type="link" size="small" @click.stop>
                                <a-icon type="more" />
                            </a-button>
                            <a-menu slot="overlay">
                                <a-menu-item @click="selectTemplate(record)">
                                    <a-icon type="eye" /> {{ $t('whatsapp_business_api.templateListPreview') }}
                                </a-menu-item>
                                <a-menu-item
                                    v-if="canEditTemplate(record)"
                                    @click="editTemplate(record)"
                                >
                                    <a-icon type="edit" /> {{ $t('whatsapp_business_api.templateListEdit') }}
                                </a-menu-item>
                                <a-menu-item @click="confirmDelete(record)">
                                    <a-icon type="delete" /> {{ $t('whatsapp_business_api.templateListDelete') }}
                                </a-menu-item>
                            </a-menu>
                        </a-dropdown>
                    </template>
                </a-table>
            </div>

            <div class="preview-panel" v-if="selectedTemplate">
                <div class="preview-header">
                    <h3>{{ $t('whatsapp_business_api.templateListPreviewTitle') }}</h3>
                    <a-button type="link" @click="selectedTemplate = null">
                        <a-icon type="close" />
                    </a-button>
                </div>
                <div class="preview-content">
                    <WhatsappMessagePreview
                        :template-type="getPreviewParams.templateType"
                        :body="getPreviewParams.body"
                        :footer="getPreviewParams.footer"
                        :header-media-type="getPreviewParams.headerType"
                        :header-text="getPreviewParams.headerText"
                        :buttons="getPreviewParams.buttons"
                        :headerMediaURL="getPreviewParams.headerMediaURL"
                        :cards="getPreviewParams.cards"
                        :limited-time-offer="getPreviewParams.limitedTimeOffer"
                        :catalogue-sub-type="getPreviewParams.catalogueSubType"
                    />
                </div>
                <div class="preview-details">
                    <div class="detail-item">
                        <label>{{ $t('whatsapp_business_api.templateListNameLabel') }}</label>
                        <span>{{ getTemplateName(selectedTemplate) }}</span>
                    </div>
                    <div class="detail-item">
                        <label>{{ $t('whatsapp_business_api.templateListIdLabel') }}</label>
                        <a-tooltip :title="copiedId === getTemplateId(selectedTemplate) ? $t('whatsapp_business_api.templateListCopied') : $t('whatsapp_business_api.templateListCopy')">
                            <span
                                class="template-id-detail"
                                :class="{ 'copied': copiedId === getTemplateId(selectedTemplate) }"
                                @click="copyTemplateId(selectedTemplate)"
                            >
                                <a-icon type="copy" /> {{ getTemplateId(selectedTemplate) }}
                            </span>
                        </a-tooltip>
                    </div>
                    <div class="detail-item">
                        <label>{{ $t('whatsapp_business_api.templateListCategoryLabel') }}</label>
                        <span>{{ getCategoryDisplay(selectedTemplate) }}</span>
                    </div>
                    <div class="detail-item">
                        <label>{{ $t('whatsapp_business_api.templateListLanguageLabel') }}</label>
                        <span>{{ getLanguageDisplay(selectedTemplate) }}</span>
                    </div>
                    <div class="detail-item">
                        <label>{{ $t('whatsapp_business_api.templateListStatusLabel') }}</label>
                        <span :class="getStatusClass(getTemplateStatus(selectedTemplate))">
                            {{ getStatusText(getTemplateStatus(selectedTemplate)) }}
                        </span>
                    </div>
                    <div class="detail-item" v-if="getRejectionInfo(selectedTemplate)">
                        <label>{{ $t('whatsapp_business_api.templateListReasonLabel') }}</label>
                        <span class="rejection-text">{{ getRejectedReason(selectedTemplate) }}</span>
                    </div>
                    <div class="detail-item" v-if="getRejectionRecommendation(selectedTemplate)">
                        <label>{{ $t('whatsapp_business_api.templateListRecommendationLabel') }}</label>
                        <span class="recommendation-text">{{ getRejectionRecommendation(selectedTemplate) }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import _ from "lodash";
import WABAMenuTypes from "./WABAMenuTypes";
import { mapActions, mapState } from "vuex";
import WhatsappMessagePreview from "../../WhatsappBusinessGupshup/WhatsappMessagePreview.vue";
import { sendSocketMessage, socketMessageStream } from "../../../../utils/socketStream";

const LANGUAGE_MAP = {
    en: "English",
    en_US: "English (US)",
    en_GB: "English (UK)",
    tr: "Turkish",
    de: "German",
    fr: "French",
    es: "Spanish",
    it: "Italian",
    pt_BR: "Portuguese (BR)",
    ar: "Arabic",
    ru: "Russian",
};

export default Vue.extend({
    name: "TemplateMessageList",
    components: { WhatsappMessagePreview },
    props: {
        value: { required: true },
        changePage: { type: Function, default: () => {} },
    },
    data() {
        return {
            selectedTemplate: null,
            subscriber: null,
            templates: [],
            searchQuery: "",
            categoryFilter: "",
            statusFilter: "",
            refreshing: false,
            deleting: false,
            copiedId: null,
            tablePagination: {
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
                showQuickJumper: true,
                position: "bottom",
            },
        };
    },
    computed: {
        ...mapState({
            socketConnected: (state) => state.socketStatus.socket_connected,
        }),
        columns() {
            return [
                {
                    title: this.$t("whatsapp_business_api.templateListColumnName"),
                    dataIndex: "name",
                    key: "name",
                    scopedSlots: { customRender: "name" },
                    width: "40%",
                },
                {
                    title: this.$t("whatsapp_business_api.templateListColumnCategory"),
                    dataIndex: "category",
                    key: "category",
                    scopedSlots: { customRender: "category" },
                    width: "15%",
                },
                {
                    title: this.$t("whatsapp_business_api.templateListColumnStatus"),
                    dataIndex: "status",
                    key: "status",
                    scopedSlots: { customRender: "status" },
                    width: "25%",
                },
                {
                    title: this.$t("whatsapp_business_api.templateListColumnLastEdited"),
                    dataIndex: "lastEdited",
                    key: "lastEdited",
                    scopedSlots: { customRender: "lastEdited" },
                    width: "15%",
                },
                {
                    title: "",
                    key: "actions",
                    scopedSlots: { customRender: "actions" },
                    width: "5%",
                    align: "right",
                },
            ];
        },
        filteredTemplates() {
            return this.templates.filter((template) => {
                const name = this.getTemplateName(template).toLowerCase();
                const category = this.getTemplateCategory(template);
                const status = this.getTemplateStatus(template);

                const matchesSearch = name.includes(this.searchQuery.toLowerCase());
                const matchesCategory = !this.categoryFilter || category === this.categoryFilter;
                const matchesStatus = !this.statusFilter || status === this.statusFilter;

                return matchesSearch && matchesCategory && matchesStatus;
            });
        },
        getPreviewParams() {
            if (!this.selectedTemplate) return {};
            return this.getTemplateParameters(this.selectedTemplate);
        },
    },
    watch: {
        filteredTemplates: {
            handler(newVal) {
                // Filtre değişince ilk item seçili olsun
                if (newVal && newVal.length > 0 && !this.selectedTemplate) {
                    this.selectedTemplate = newVal[0];
                }
            },
            immediate: true,
        },
    },
    methods: {
        ...mapActions({
            getTemplates: "whatsapp/getTemplates",
        }),
        getTemplateName(template) {
            return template.name || template.elementName || "";
        },
        getTemplateId(template) {
            return template.id || template.externalId || "";
        },
        getTemplateCategory(template) {
            return template.category || "";
        },
        getCategoryDisplay(template) {
            var cat = this.getTemplateCategory(template);
            if (cat === "MARKETING") return this.$t("whatsapp_business_api.marketing");
            if (cat === "UTILITY") return this.$t("whatsapp_business_api.utility");
            if (cat === "AUTHENTICATION") return this.$t("whatsapp_business_api.authentication");
            return cat;
        },
        getTemplateStatus(template) {
            return template.status || "";
        },
        getTemplateLanguage(template) {
            return template.language || template.languageCode || "";
        },
        getLanguageDisplay(template) {
            const lang = this.getTemplateLanguage(template);
            return LANGUAGE_MAP[lang] || lang;
        },
        getBodyPreview(template) {
            if (template.format === "meta" && template.components) {
                const bodyComponent = template.components.find((c) => c.type === "BODY");
                if (bodyComponent && bodyComponent.text) {
                    return bodyComponent.text.substring(0, 80) + (bodyComponent.text.length > 80 ? "..." : "");
                }
            }
            if (template.meta) {
                try {
                    const meta = typeof template.meta === "string" ? JSON.parse(template.meta) : template.meta;
                    const text = meta.data || meta.example || "";
                    return text.substring(0, 80) + (text.length > 80 ? "..." : "");
                } catch (e) {
                    return "";
                }
            }
            return "";
        },
        getCategoryColor(category) {
            const colors = {
                MARKETING: "blue",
                UTILITY: "green",
                AUTHENTICATION: "orange",
            };
            return colors[category] || "default";
        },
        getStatusClass(status) {
            const classes = {
                APPROVED: "status-approved",
                PENDING: "status-pending",
                REJECTED: "status-rejected",
                FAILED: "status-rejected",
            };
            return classes[status] || "status-default";
        },
        getStatusText(status) {
            const texts = {
                APPROVED: this.$t("whatsapp_business_api.templateListStatusActive"),
                PENDING: this.$t("whatsapp_business_api.templateListStatusInReview"),
                REJECTED: this.$t("whatsapp_business_api.templateListStatusRejected"),
                FAILED: this.$t("whatsapp_business_api.templateListStatusFailed"),
            };
            return texts[status] || status;
        },
        getQualityScore(template) {
            if (template.quality_score) {
                if (typeof template.quality_score === "object") {
                    return template.quality_score.score;
                }
                return template.quality_score;
            }
            return null;
        },
        getQualityClass(template) {
            const score = this.getQualityScore(template);
            if (score === "GREEN") return "quality-green";
            if (score === "YELLOW") return "quality-yellow";
            if (score === "RED") return "quality-red";
            return "quality-unknown";
        },
        getQualityText(template) {
            const score = this.getQualityScore(template);
            if (score === "GREEN") return this.$t("whatsapp_business_api.templateListQualityHigh");
            if (score === "YELLOW") return this.$t("whatsapp_business_api.templateListQualityMedium");
            if (score === "RED") return this.$t("whatsapp_business_api.templateListQualityLow");
            return "";
        },
        getRejectionInfo(template) {
            if (template.rejectionInfo && (template.rejectionInfo.reason || template.rejectionInfo.recommendation)) {
                return template.rejectionInfo;
            }
            if (template.rejected_reason && template.rejected_reason !== "NONE") {
                return { reason: template.rejected_reason };
            }
            return null;
        },
        getRejectedReason(template) {
            if (template.rejectionInfo && template.rejectionInfo.reason) {
                return template.rejectionInfo.reason;
            }
            if (template.rejected_reason && template.rejected_reason !== "NONE") {
                return template.rejected_reason;
            }
            return null;
        },
        getRejectionRecommendation(template) {
            if (template.rejectionInfo && template.rejectionInfo.recommendation) {
                return template.rejectionInfo.recommendation;
            }
            return null;
        },
        formatDate(date) {
            if (!date) return "-";
            return this.$moment(date).format("DD MMM YYYY");
        },
        selectTemplate(record) {
            this.selectedTemplate = record;
        },
        getRowClassName(record) {
            return this.selectedTemplate && this.selectedTemplate.id === record.id ? "selected-row" : "";
        },
        customRow(record) {
            return {
                on: {
                    click: () => {
                        this.selectTemplate(record);
                    },
                },
            };
        },
        getChannelTemplates() {
            this.getTemplates(this.value._id).then((templates) => {
                this.templates = templates;
                // İlk item'ı seç
                if (templates && templates.length > 0) {
                    this.selectedTemplate = templates[0];
                }
            });
        },
        refreshTemplates() {
            this.refreshing = true;
            sendSocketMessage("whatsapp refresh-template", {
                channelId: this.value._id,
                siteId: this.value.siteId,
                channelType: this.value.type,
            });
            this.subscriber = socketMessageStream.subscribe((message) => {
                if (message.cmd === "whatsapp_template_refreshed") {
                    this.getChannelTemplates();
                    this.refreshing = false;
                    if (this.subscriber) {
                        this.subscriber.unsubscribe();
                    }
                }
            });
        },
        getTemplateParameters(template) {
            // Meta format için
            if (template.format === "meta" && template.components) {
                const components = template.components || [];
                const headerComponent = components.find((c) => c.type === "HEADER");
                const bodyComponent = components.find((c) => c.type === "BODY");
                const footerComponent = components.find((c) => c.type === "FOOTER");
                const buttonsComponent = components.find((c) => c.type === "BUTTONS");
                const carouselComponent = components.find((c) => c.type === "CAROUSEL");
                const ltoComponent = components.find((c) => c.type === "LIMITED_TIME_OFFER");

                let headerType = "TEXT";
                let headerMediaURL = null;
                if (headerComponent) {
                    headerType = headerComponent.format;
                    headerMediaURL = _.get(template, "mediaPayload.headerAsset.cdnUrl");
                }

                // Template type detection
                let detectedTemplateType = "CUSTOM_MESSAGE";
                if (carouselComponent) {
                    detectedTemplateType = "CAROUSEL";
                } else if (ltoComponent) {
                    detectedTemplateType = "LIMITED_TIME_OFFER";
                } else if (headerType === "PRODUCT") {
                    detectedTemplateType = "CATALOGUE";
                } else if (components.some(c => c.type === "call_permission_request")) {
                    detectedTemplateType = "CALL_PERMISSION";
                } else if (buttonsComponent && buttonsComponent.buttons) {
                    const hasCopyCode = buttonsComponent.buttons.some(btn => btn.type === "COPY_CODE");
                    if (hasCopyCode && template.category === "MARKETING") {
                        // COUPON_CODE detection - eğer sadece 1 COPY_CODE varsa ve text customize edilememişse
                        const copyCodeButtons = buttonsComponent.buttons.filter(btn => btn.type === "COPY_CODE");
                        if (copyCodeButtons.length === 1) {
                            detectedTemplateType = "COUPON_CODE";
                        }
                    }
                }

                // Carousel cards için preview data hazırla
                let carouselCards = [];
                if (carouselComponent && carouselComponent.cards) {
                    carouselCards = carouselComponent.cards.map((card, index) => {
                        const cardComponents = card.components || [];
                        const cardHeader = cardComponents.find(c => c.type === "HEADER");
                        const cardBody = cardComponents.find(c => c.type === "BODY");
                        const cardButtons = cardComponents.find(c => c.type === "BUTTONS");

                        let mediaUrl = _.get(template, `mediaPayload.cardAssets[${index}].cdnUrl`);

                        return {
                            headerType: cardHeader ? cardHeader.format : "IMAGE",
                            mediaUrl: mediaUrl,
                            sampleText: cardBody ? cardBody.text : "",
                            buttons: cardButtons ? cardButtons.buttons : [],
                        };
                    });
                }

                return {
                    templateType: detectedTemplateType,
                    headerType,
                    headerMediaURL,
                    headerText: headerComponent && headerComponent.text ? headerComponent.text : null,
                    body: bodyComponent ? bodyComponent.text : "",
                    footer: footerComponent ? footerComponent.text : null,
                    buttons: buttonsComponent ? buttonsComponent.buttons : [],
                    cards: carouselCards,
                    limitedTimeOffer: ltoComponent ? ltoComponent.limited_time_offer : null,
                    catalogueSubType: template.catalogueSubType || 'CATALOGUE',
                };
            }

            // Gupshup format için
            try {
                const metaObject = typeof template.meta === "string" ? JSON.parse(template.meta) : template.meta || {};
                const containerMetaObj = typeof template.containerMeta === "string"
                    ? JSON.parse(template.containerMeta)
                    : template.containerMeta || {};
                const headerMediaURL = (containerMetaObj.sampleMedia || "").startsWith("http")
                    ? containerMetaObj.sampleMedia
                    : template.asset && (template.asset.v2Format && template.asset.cdnUrl
                        ? template.asset.cdnUrl
                        : template.asset.path
                            ? `https://static.supsis.live/${template.asset.path}${template.asset.id}.${template.asset.ext}`
                            : null);

                return {
                    headerMediaURL,
                    headerType: template.templateType || "TEXT",
                    headerMediaType: template.templateType || "TEXT",
                    headerText: _.get(containerMetaObj, "header"),
                    footer: _.get(containerMetaObj, "footer"),
                    body: _.get(metaObject, "example") || metaObject.data || "",
                    buttons: _.get(containerMetaObj, "buttons") || template.buttons || [],
                    cards: _.get(containerMetaObj, "cards", []),
                };
            } catch (e) {
                return {
                    headerType: "TEXT",
                    body: "",
                    footer: null,
                    headerText: null,
                    buttons: [],
                    cards: [],
                };
            }
        },
        gotoCreatePage() {
            this.changePage(WABAMenuTypes.MENU_TYPES.NEW_TEMPLATE);
        },
        canEditTemplate(template) {
            // Meta API kurallarına göre: Sadece APPROVED, REJECTED veya PAUSED status'üne sahip template'ler edit edilebilir
            const status = this.getTemplateStatus(template);
            const editableStatuses = ["APPROVED", "REJECTED", "PAUSED"];
            return template.format === "meta" && editableStatuses.includes(status);
        },
        editTemplate(template) {
            // Edit mode için template'i ve channel'i geçir
            this.$emit("edit-template", template);
        },
        copyTemplateId(template) {
            const id = this.getTemplateId(template);
            if (!id) return;

            navigator.clipboard.writeText(id)
                .then(() => {
                    this.copiedId = id;
                    setTimeout(() => {
                        this.copiedId = null;
                    }, 2000);
                })
                .catch((err) => {
                    console.error("ID kopyalanırken hata oluştu:", err);
                    this.$message.error(this.$t("whatsapp_business_api.templateListCopyIdError"));
                });
        },
        confirmDelete(template) {
            var self = this;
            var templateName = this.getTemplateName(template);
            this.$confirm({
                title: this.$t("whatsapp_business_api.templateListDeleteConfirmTitle"),
                content: this.$t("whatsapp_business_api.templateListDeleteConfirmContent", { name: templateName }),
                okText: this.$t("whatsapp_business_api.templateListDeleteConfirmOk"),
                okType: "danger",
                cancelText: this.$t("whatsapp_business_api.templateListDeleteConfirmCancel"),
                onOk: function() {
                    self.deleting = true;

                    var deleteSubscriber = socketMessageStream.subscribe(function(message) {
                        console.log("whatsapp_delete_template_message_response debug message", message);
                        if (message.cmd === "whatsapp_delete_template_message_response" && message.data.templateName === templateName) {
                            self.deleting = false;
                            if (message.data.success) {
                                self.templates = self.templates.filter(function(t) { return t.id !== template.id; });
                                self.$message.success(self.$t("whatsapp_business_api.templateListDeletedSuccess", { name: templateName }));
                                if (self.selectedTemplate && self.selectedTemplate.id === template.id) {
                                    self.selectedTemplate = null;
                                }
                            } else {
                                self.$message.error(message.data.error || self.$t("whatsapp_business_api.templateListDeleteFailed"));
                            }
                            deleteSubscriber.unsubscribe();
                        }
                    });
                    sendSocketMessage("whatsapp delete-template-message", {
                        channelId: self.value._id,
                        siteId: self.value.siteId,
                        channelType: self.value.type,
                        templateName: templateName,
                    });
                },
            });
        },
    },
    mounted() {
        this.getChannelTemplates();
    },
    beforeDestroy() {
        if (this.subscriber) {
            this.subscriber.unsubscribe();
        }
    },
});
</script>

<style scoped lang="scss">
.template-message-list {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #f5f7fa;

    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px 24px;
        background: white;
        border-bottom: 1px solid #e8e8e8;

        .header-left {
            h2 {
                margin: 0 0 4px 0;
                font-size: 20px;
                font-weight: 600;
                color: #1a1a1a;
            }

            .template-count {
                font-size: 13px;
                color: #666;
            }
        }
    }

    .filters-bar {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px 24px;
        background: white;
        border-bottom: 1px solid #e8e8e8;

        .spacer {
            flex: 1;
        }

        .pagination-info {
            font-size: 13px;
            color: #666;
        }
    }

    .content-area {
        display: flex;
        flex: 1;
        overflow: hidden;

        .table-container {
            flex: 1;
            padding: 20px 24px;
            overflow: auto;

            ::v-deep .ant-table {
                background: white;
                border-radius: 8px;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

                .ant-table-thead > tr > th {
                    background: #fafafa;
                    font-weight: 600;
                    color: #666;
                    font-size: 11px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 12px 16px;
                }

                .ant-table-tbody > tr {
                    cursor: pointer;
                    transition: background 0.2s;

                    > td {
                        padding: 16px;
                        vertical-align: top;
                    }

                    &:hover {
                        background: #f5f5f5;
                    }

                    &.selected-row {
                        background: #e6f7ff;
                    }
                }

                .ant-table-pagination {
                    margin: 16px;
                }
            }
        }

        .preview-panel {
            width: 380px;
            background: white;
            border-left: 1px solid #e8e8e8;
            display: flex;
            flex-direction: column;

            .preview-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 16px 20px;
                border-bottom: 1px solid #e8e8e8;

                h3 {
                    margin: 0;
                    font-size: 16px;
                    font-weight: 600;
                }
            }

            .preview-content {
                flex: 1;
                padding: 20px;
                display: flex;
                justify-content: center;
                overflow: auto;
            }

            .preview-details {
                padding: 16px 20px;
                border-top: 1px solid #e8e8e8;
                background: #fafafa;

                .detail-item {
                    display: flex;
                    margin-bottom: 8px;

                    &:last-child {
                        margin-bottom: 0;
                    }

                    label {
                        width: 100px;
                        font-weight: 500;
                        color: #666;
                        font-size: 13px;
                        flex-shrink: 0;
                    }

                    span {
                        flex: 1;
                        font-size: 13px;
                        color: #1a1a1a;
                    }

                    .template-id-detail {
                        font-family: monospace;
                        font-size: 12px;
                        color: #666;
                        cursor: pointer;
                        padding: 4px 8px;
                        border-radius: 4px;
                        border: 1px solid transparent;
                        transition: all 0.2s;
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        max-width: 100%;
                        word-break: break-all;

                        &:hover {
                            background: #f0f0f0;
                            color: #1890ff;
                            border-color: #d9d9d9;
                        }

                        &.copied {
                            background: #f6ffed;
                            color: #52c41a;
                            border-color: #b7eb8f;
                        }

                        .anticon {
                            font-size: 12px;
                            flex-shrink: 0;
                            line-height: 1;
                        }
                    }

                    .rejection-text {
                        color: #cf1322;
                    }

                    .recommendation-text {
                        color: #666;
                        font-style: italic;
                    }
                }
            }
        }
    }

    .template-name-cell {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .name {
            font-weight: 600;
            color: #1a1a1a;
            font-size: 14px;
        }

        .template-id {
            font-size: 11px;
            color: #999;
            font-family: monospace;
            cursor: pointer;
            padding: 2px 6px;
            border-radius: 4px;
            border: 1px solid transparent;
            transition: all 0.2s;
            display: inline-flex;
            align-items: center;
            gap: 4px;
            max-width: fit-content;

            &:hover {
                background: #f0f0f0;
                color: #1890ff;
                border-color: #d9d9d9;
            }

            &.copied {
                background: #f6ffed;
                color: #52c41a;
                border-color: #b7eb8f;
            }

            .anticon {
                font-size: 11px;
                flex-shrink: 0;
            }
        }

        .language {
            font-size: 12px;
            color: #666;
        }

        .preview-text {
            font-size: 12px;
            color: #999;
            margin-top: 4px;
            line-height: 1.4;
        }
    }

    .status-cell {
        display: flex;
        flex-direction: column;
        gap: 6px;

        .status-content {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .status-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 4px 12px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 500;

            .pending-icon {
                animation: spin 1s linear infinite;
            }

            &.status-approved {
                background: #d4edda;
                color: #155724;
            }

            &.status-pending {
                background: #cce5ff;
                color: #004085;
            }

            &.status-rejected {
                background: #f8d7da;
                color: #721c24;
            }

            &.status-default {
                background: #e2e3e5;
                color: #383d41;
            }
        }

        .quality-badge {
            padding: 2px 8px;
            border-radius: 10px;
            font-size: 11px;
            font-weight: 500;

            &.quality-green {
                background: #d4edda;
                color: #155724;
            }

            &.quality-yellow {
                background: #fff3cd;
                color: #856404;
            }

            &.quality-red {
                background: #f8d7da;
                color: #721c24;
            }

            &.quality-unknown {
                display: none;
            }
        }

        .rejection-info {
            background: #fff1f0;
            padding: 8px;
            border-radius: 4px;
            border-left: 3px solid #cf1322;

            .rejection-reason {
                font-size: 11px;
                color: #cf1322;
                line-height: 1.4;

                strong {
                    font-weight: 600;
                }
            }

            .rejection-recommendation {
                font-size: 11px;
                color: #666;
                margin-top: 4px;
                line-height: 1.4;
                font-style: italic;
            }
        }
    }
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
